import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Sparkles, Lock } from "lucide-react";
import VaultCard from "./cards/VaultCard";
import VaultCardDetailModal from "./cards/VaultCardDetailModal";

const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#00ff88",
  rare: "#22d3ee",
  mythic: "#a78bfa",
  classified: "#ff3b30",
  divine: "#ffd700",
  celestial: "#d946ef",
};

const RARITY_BLURB = {
  common:     "A solid pull. Add it to the wall.",
  uncommon:   "Nice one — not many copies of this in circulation.",
  rare:       "Three cheers. This is a Rare pull.",
  mythic:     "Mythic. You're holding something most collectors never see.",
  classified: "CLASSIFIED. Top-shelf. The Vault just unsealed itself for you.",
  divine:     "DIVINE. Twenty-five copies of this card will EVER exist. You hold one.",
  celestial:  "CELESTIAL. Ten copies of this card will EVER exist. The Vault has refracted itself for you.",
};

// Confetti palette per-rarity — the burst color-matches the card's tier.
// Common/Uncommon stay subtle; the rarer the pull, the more chromatic and
// the higher the particle count.
const RARITY_BURST = {
  common:     { count: 24, colors: ["#94a3b8", "#cbd5e1", "#ffffff"] },
  uncommon:   { count: 36, colors: ["#00ff88", "#22d3ee", "#ffffff"] },
  rare:       { count: 60, colors: ["#22d3ee", "#67e8f9", "#a5f3fc", "#ffffff"] },
  mythic:     { count: 90, colors: ["#a78bfa", "#c4b5fd", "#f0abfc", "#22d3ee", "#ffffff"] },
  classified: { count: 130, colors: ["#ff3b30", "#ff9500", "#ffd60a", "#ffffff"] },
  divine:     { count: 220, colors: ["#ffd700", "#fff2a8", "#ffec5e", "#ffffff", "#fff7c2"] },
  // Celestial — full prismatic spectrum, dense burst (twice Divine).
  celestial:  { count: 320, colors: ["#ff5f5f", "#ffb45f", "#ffe75f", "#74ff8a", "#5fb4ff", "#aa7bff", "#ff7bd0", "#ffffff"] },
};

/**
 * Confetti — pure CSS/JSX burst overlay rendered above the modal backdrop.
 * Each piece is a tiny absolutely-positioned div with a randomized
 * launch angle, distance, rotation, and color. Mounted ONLY when the card
 * has flipped face-up so the burst sells the reveal.
 *
 * We don't use a confetti library because (a) we already have all the
 * primitives we need, and (b) keeping it bundle-free matters for a site
 * that already ships a heavy 3D Iceberg + WarRoom canvas.
 */
function ConfettiBurst({ active, rarity = "common" }) {
  const config = RARITY_BURST[rarity] || RARITY_BURST.common;
  // Memoize per-burst so re-renders don't reshuffle a running animation.
  const pieces = useMemo(() => {
    if (!active) return [];
    return Array.from({ length: config.count }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 180 + Math.random() * 360;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist - 80;  // bias upward so they "explode" up first
      const spin = (Math.random() - 0.5) * 720;
      const size = 4 + Math.random() * 6;
      const delay = Math.random() * 250;
      const dur = 1400 + Math.random() * 900;
      const color = config.colors[i % config.colors.length];
      const shape = Math.random() > 0.5 ? "0" : "9999px";
      return { dx, dy, spin, size, delay, dur, color, shape, id: i };
    });
  }, [active, config.count, config.colors]);

  if (!active) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[201] overflow-hidden" data-testid="card-reward-confetti">
      <style>{`
        @keyframes vc-confetti-fly {
          0%   { transform: translate3d(0,0,0) rotate(0deg);     opacity: 0; }
          12%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translate3d(var(--dx), var(--dy), 0) rotate(var(--spin)); opacity: 0; }
        }
      `}</style>
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: p.shape,
            boxShadow: `0 0 8px ${p.color}80`,
            opacity: 0,
            animation: `vc-confetti-fly ${p.dur}ms cubic-bezier(0.16, 1, 0.3, 1) ${p.delay}ms forwards`,
            ["--dx"]: `${p.dx}px`,
            ["--dy"]: `${p.dy}px`,
            ["--spin"]: `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Plays a rarity-tiered audio cue on the reveal frame. Frequencies and
 * envelope length form a deliberate ladder: a Common pull is a subtle
 * single tick, a Divine pull is a full Solfeggio chord with a Schumann
 * sub-bass heartbeat. Uses the Web Audio API directly — zero dependency
 * cost.
 *
 * Wrapped in try/catch so older browsers and autoplay restrictions just
 * no-op silently. Honors `prefers-reduced-motion` as a proxy for "user
 * dislikes sudden sensory effects".
 */
function playRarityTone(rarity) {
  try {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    // Spec per rarity. `partials` is an array of {freq, gain} that sustain
    // for `dur` seconds. `risePartials` (optional) is an array of secondary
    // partials that ring in after `riseDelay` seconds — used for Rare to
    // give it a "two-note ascending" feel.
    const SPECS = {
      common: {
        dur: 0.4, attack: 0.02, release: 0.18, master: 0.07,
        partials: [{ freq: 165, gain: 1.0, type: "triangle" }],
      },
      uncommon: {
        dur: 0.8, attack: 0.04, release: 0.4, master: 0.09,
        partials: [
          { freq: 220, gain: 1.0, type: "sine" },
          { freq: 440, gain: 0.25, type: "sine" },
        ],
      },
      rare: {
        dur: 1.4, attack: 0.04, release: 0.7, master: 0.11,
        partials: [{ freq: 285, gain: 0.85, type: "sine" }],
        risePartials: [
          { freq: 432, gain: 0.85, type: "sine" },
          { freq: 864, gain: 0.18, type: "sine" },
        ],
        riseDelay: 0.45,
      },
      mythic: {
        dur: 2.0, attack: 0.08, release: 1.0, master: 0.13,
        partials: [
          { freq: 432, gain: 0.9, type: "sine" },
          { freq: 648, gain: 0.45, type: "sine" },
          { freq: 864, gain: 0.22, type: "sine" },
        ],
      },
      classified: {
        dur: 2.6, attack: 0.1, release: 1.2, master: 0.15,
        partials: [
          { freq: 396, gain: 0.7, type: "sine" },
          { freq: 528, gain: 1.0, type: "sine" },
          { freq: 792, gain: 0.35, type: "sine" },
        ],
        tremoloHz: 5.5,  // slight wobble for the foil "shimmer" feel
        tremoloDepth: 0.18,
      },
    };

    // Divine gets the full cathedral chord with the Schumann sub-bass.
    // Kept inline (not in SPECS) because of the LFO routing.
    if (rarity === "divine") {
      const dur = 4.5;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, now);
      master.gain.linearRampToValueAtTime(0.18, now + 0.4);
      master.gain.linearRampToValueAtTime(0.16, now + dur - 1.8);
      master.gain.linearRampToValueAtTime(0, now + dur);
      master.connect(ctx.destination);
      [396, 528, 639].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        const g = ctx.createGain();
        g.gain.value = idx === 1 ? 0.55 : 0.35;
        osc.connect(g).connect(master);
        osc.start(now);
        osc.stop(now + dur);
      });
      const sub = ctx.createOscillator();
      sub.type = "sine";
      sub.frequency.value = 55;
      const subGain = ctx.createGain();
      subGain.gain.value = 0.0001;
      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.value = 7.83;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.18;
      lfo.connect(lfoGain).connect(subGain.gain);
      sub.connect(subGain).connect(master);
      sub.start(now);
      lfo.start(now);
      sub.stop(now + dur);
      lfo.stop(now + dur);
      setTimeout(() => { try { ctx.close(); } catch (_e) {} }, (dur + 0.5) * 1000);
      return;
    }

    // Celestial — the most advanced "god frequency" chord on the ladder.
    // Stacks the full Solfeggio nine-tone scale plus a bright crystalline
    // shimmer (an 8 kHz triangle held with a slow tremolo), a Schumann
    // sub-bass, AND a chime-strike on first hit. The full chord rings for
    // 6 seconds — noticeably longer than Divine — so a Celestial pull
    // feels like the air itself is humming.
    if (rarity === "celestial") {
      const dur = 6.0;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, now);
      master.gain.linearRampToValueAtTime(0.22, now + 0.5);
      master.gain.linearRampToValueAtTime(0.20, now + dur - 2.0);
      master.gain.linearRampToValueAtTime(0, now + dur);
      master.connect(ctx.destination);

      // Solfeggio 9 — the full sacred-frequency scale.
      const SOLFEGGIO = [174, 285, 396, 417, 528, 639, 741, 852, 963];
      SOLFEGGIO.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        const g = ctx.createGain();
        // 528 Hz ("Love") leads; 174 + 285 form the warm root; the upper
        // four (639, 741, 852, 963) sit quieter as harmonic shimmer.
        const gain = idx === 4 ? 0.42 : idx < 2 ? 0.30 : idx < 4 ? 0.32 : 0.18;
        g.gain.value = gain;
        osc.connect(g).connect(master);
        osc.start(now);
        osc.stop(now + dur);
      });

      // Bright crystalline shimmer — high triangle with a tremolo, rings
      // in 0.3s after the chord seats. This is the "broken glass catching
      // light" feel.
      const shimmer = ctx.createOscillator();
      shimmer.type = "triangle";
      shimmer.frequency.value = 4186; // C8 — top of the piano
      const shimmerGain = ctx.createGain();
      shimmerGain.gain.setValueAtTime(0, now + 0.3);
      shimmerGain.gain.linearRampToValueAtTime(0.07, now + 0.7);
      shimmerGain.gain.linearRampToValueAtTime(0.04, now + dur - 1.0);
      shimmerGain.gain.linearRampToValueAtTime(0, now + dur);
      const shimmerLfo = ctx.createOscillator();
      shimmerLfo.type = "sine";
      shimmerLfo.frequency.value = 6.0;
      const shimmerLfoGain = ctx.createGain();
      shimmerLfoGain.gain.value = 0.025;
      shimmerLfo.connect(shimmerLfoGain).connect(shimmerGain.gain);
      shimmer.connect(shimmerGain).connect(master);
      shimmer.start(now + 0.3);
      shimmerLfo.start(now + 0.3);
      shimmer.stop(now + dur);
      shimmerLfo.stop(now + dur);

      // Single chime strike on first hit — a 1.5 kHz triangle with a fast
      // exponential decay, like a crystal singing bowl being struck.
      const chime = ctx.createOscillator();
      chime.type = "triangle";
      chime.frequency.value = 1568;
      const chimeGain = ctx.createGain();
      chimeGain.gain.setValueAtTime(0, now);
      chimeGain.gain.linearRampToValueAtTime(0.14, now + 0.02);
      chimeGain.gain.exponentialRampToValueAtTime(0.001, now + 1.6);
      chime.connect(chimeGain).connect(master);
      chime.start(now);
      chime.stop(now + 1.7);

      // Schumann sub-bass for that "the room is vibrating" body.
      const sub = ctx.createOscillator();
      sub.type = "sine";
      sub.frequency.value = 55;
      const subGain = ctx.createGain();
      subGain.gain.value = 0.0001;
      const subLfo = ctx.createOscillator();
      subLfo.type = "sine";
      subLfo.frequency.value = 7.83;
      const subLfoGain = ctx.createGain();
      subLfoGain.gain.value = 0.22;
      subLfo.connect(subLfoGain).connect(subGain.gain);
      sub.connect(subGain).connect(master);
      sub.start(now);
      subLfo.start(now);
      sub.stop(now + dur);
      subLfo.stop(now + dur);

      setTimeout(() => { try { ctx.close(); } catch (_e) {} }, (dur + 0.5) * 1000);
      return;
    }

    const spec = SPECS[rarity] || SPECS.common;
    const dur = spec.dur;

    // Master envelope.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(spec.master, now + spec.attack);
    master.gain.linearRampToValueAtTime(spec.master * 0.9, now + dur - spec.release);
    master.gain.linearRampToValueAtTime(0, now + dur);
    master.connect(ctx.destination);

    // Optional tremolo (classified shimmer).
    let outNode = master;
    if (spec.tremoloHz) {
      const tremGain = ctx.createGain();
      tremGain.gain.value = 1;
      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.value = spec.tremoloHz;
      const lfoDepth = ctx.createGain();
      lfoDepth.gain.value = spec.tremoloDepth;
      lfo.connect(lfoDepth).connect(tremGain.gain);
      tremGain.connect(master);
      outNode = tremGain;
      lfo.start(now);
      lfo.stop(now + dur);
    }

    // Primary partials — start immediately.
    spec.partials.forEach((p) => {
      const osc = ctx.createOscillator();
      osc.type = p.type || "sine";
      osc.frequency.value = p.freq;
      const g = ctx.createGain();
      g.gain.value = p.gain;
      osc.connect(g).connect(outNode);
      osc.start(now);
      osc.stop(now + dur);
    });

    // Rise partials — ring in after `riseDelay` for the two-note feel.
    if (spec.risePartials && spec.riseDelay != null) {
      const riseStart = now + spec.riseDelay;
      spec.risePartials.forEach((p) => {
        const osc = ctx.createOscillator();
        osc.type = p.type || "sine";
        osc.frequency.value = p.freq;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, riseStart);
        g.gain.linearRampToValueAtTime(p.gain, riseStart + 0.08);
        osc.connect(g).connect(outNode);
        osc.start(riseStart);
        osc.stop(now + dur);
      });
    }

    setTimeout(() => { try { ctx.close(); } catch (_e) {} }, (dur + 0.3) * 1000);
  } catch (_e) {
    /* silent — audio is a bonus, never a blocker */
  }
}

/**
 * CardRewardModal — full-screen reveal for a freshly-minted Vault Card.
 * Three stages drive the animation:
 *   1. "loading"  — initial state, modal mounts invisible
 *   2. "back"     — Vault-sealed BACK of the card appears, rarity glow pulses
 *   3. "flipped"  — card flips 180° on Y axis, revealing the actual art
 *
 * The user can click the back to flip early, otherwise it auto-flips after
 * a short suspense window. The headline and metadata cross-fade in once the
 * front face is showing.
 */
export default function CardRewardModal({ open, reward, source = "arg", onClose, streak, streakBonus }) {
  const [stage, setStage] = useState("loading"); // loading → back → flipped
  const [showDetail, setShowDetail] = useState(false);
  // Track whether we've already fired the divine audio/flash so a re-render
  // (e.g. parent re-render after coin update) doesn't double-trigger it.
  const fxFiredRef = useRef(false);

  useEffect(() => {
    if (!open) { setStage("loading"); setShowDetail(false); fxFiredRef.current = false; return; }
    // Stage 1: show the back of the card
    const t1 = setTimeout(() => setStage("back"), 250);
    // Stage 2: auto-flip after the user has had a moment to register
    const t2 = setTimeout(() => setStage("flipped"), 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [open, reward?.card_id]);

  // Tiered reveal FX — fires the FIRST time the card lands on its
  // face-up side. Every rarity plays a tone (Common = subtle tick all
  // the way up to Divine = full Solfeggio chord). Classified + Divine
  // additionally trigger a screen flash.
  const cardRarity = reward?.card_rarity || reward?.card_def?.rarity || "common";
  useEffect(() => {
    if (!open || stage !== "flipped" || fxFiredRef.current) return;
    fxFiredRef.current = true;
    playRarityTone(cardRarity);
  }, [open, stage, cardRarity]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !reward) return null;
  const card = reward.card_def || {
    id: reward.card_id,
    name: reward.card_name,
    rarity: reward.card_rarity,
    icon: "key",
    subject: "concept",
    flavor: "",
    edition_limit: reward.edition_limit,
  };
  const color = RARITY_COLOR[card.rarity] || "#ffd60a";
  // Headline tailored to the path that earned the card. Default to the
  // generic "vault unsealed" hook so unknown sources still feel cinematic.
  const headline = (
    source === "crank"    ? "CRANK TEST CRACKED · CARD PULLED"
  : source === "arg"      ? "CODE CRACKED · CARD PULLED"
  : source === "daily"    ? "DAILY PULL · INTAKE CONFIRMED"
  : source === "roll"     ? "ROLL THE DIE · CARD MATERIALIZED"
  : source === "artifact" ? "ARTIFACT MINTED · CARD FORGED"
  : source === "trade"    ? "TRADE ACCEPTED · NEW CARD ADDED"
  : source === "awakening" ? "GREAT AWAKENING · CARD CLAIMED"
  :                         "VAULT UNSEALED · CARD ACQUIRED"
  );
  const flipped = stage === "flipped";
  const visible = stage !== "loading";

  // Click the back to flip early — satisfying for impatient users.
  // Click the front (post-flip) to open the full detail view — same view
  // used on the Vault Cards page so the styling is identical.
  const onCardClick = () => {
    if (stage === "back") setStage("flipped");
    else if (stage === "flipped") setShowDetail(true);
  };

  // When the detail modal is open, render ONLY the detail modal so the
  // reward modal's radial glow + confetti chrome doesn't bleed through
  // behind it. Closing the detail modal also dismisses the reward popup.
  if (showDetail) {
    return (
      <VaultCardDetailModal
        card={card}
        editionNumber={reward.edition_number}
        copies={reward.copies_owned}
        onClose={() => { setShowDetail(false); onClose?.(); }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="card-reward-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        background: `radial-gradient(circle at center, ${color}22 0%, rgba(0,0,0,0.92) 60%)`,
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Confetti burst — mounts only once the card flips face-up, so the
          rain of sparks is timed with the reveal. Particle count + colors
          scale with the rarity, peaking at Divine. */}
      <ConfettiBurst active={flipped} rarity={card.rarity} />

      {/* CLASSIFIED — short red foil flash. Subtler than the Divine flash;
          no god-rays. Lets Classified still feel "above the line" without
          stealing Divine's thunder. */}
      {flipped && card.rarity === "classified" && (
        <>
          <style>{`
            @keyframes vc-classified-flash {
              0%   { opacity: 0; }
              18%  { opacity: 0.7; }
              100% { opacity: 0; }
            }
          `}</style>
          <div
            className="pointer-events-none fixed inset-0 z-[199]"
            style={{
              background: "radial-gradient(circle at center, rgba(255,200,180,0.6) 0%, rgba(255,59,48,0.45) 28%, transparent 65%)",
              animation: "vc-classified-flash 600ms ease-out forwards",
            }}
            data-testid="card-reward-classified-flash"
          />
        </>
      )}

      {/* CELESTIAL — full-spectrum prismatic rainbow flash + cracked-glass
          shard ring. Sits one tier above Divine; the flash is brighter and
          the rays are sharper/more geometric than Divine's gold sweep. */}
      {flipped && card.rarity === "celestial" && (
        <>
          <style>{`
            @keyframes vc-celestial-flash {
              0%   { opacity: 0; }
              12%  { opacity: 1.0; }
              100% { opacity: 0; }
            }
            @keyframes vc-celestial-burst {
              0%   { transform: rotate(0deg) scale(0.4); opacity: 0; }
              20%  { opacity: 0.85; }
              100% { transform: rotate(70deg) scale(1.6); opacity: 0; }
            }
            @keyframes vc-celestial-ring {
              0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0; }
              25%  { opacity: 0.9; }
              100% { transform: translate(-50%, -50%) scale(3.0); opacity: 0; }
            }
          `}</style>
          {/* Hot-white core with rainbow falloff. */}
          <div
            className="pointer-events-none fixed inset-0 z-[199]"
            style={{
              background:
                "radial-gradient(circle at center, rgba(255,255,255,1.0) 0%, rgba(255,180,255,0.7) 18%, rgba(180,255,255,0.55) 32%, rgba(255,255,255,0.0) 60%)",
              animation: "vc-celestial-flash 950ms ease-out forwards",
            }}
            data-testid="card-reward-celestial-flash"
          />
          {/* Prismatic conic god-rays — rainbow wedges sweep outward. */}
          <div
            className="pointer-events-none fixed inset-0 z-[199] flex items-center justify-center"
            aria-hidden
          >
            <div
              style={{
                width: 1600, height: 1600,
                background:
                  "conic-gradient(from 0deg, transparent 0deg, rgba(255,100,100,0.30) 6deg, transparent 14deg, transparent 40deg, rgba(255,200,100,0.30) 46deg, transparent 54deg, transparent 80deg, rgba(255,255,120,0.30) 86deg, transparent 94deg, transparent 120deg, rgba(120,255,150,0.30) 126deg, transparent 134deg, transparent 160deg, rgba(100,200,255,0.30) 166deg, transparent 174deg, transparent 200deg, rgba(160,130,255,0.30) 206deg, transparent 214deg, transparent 240deg, rgba(255,130,220,0.30) 246deg, transparent 254deg, transparent 360deg)",
                mixBlendMode: "screen",
                filter: "blur(2px)",
                animation: "vc-celestial-burst 2400ms ease-out forwards",
              }}
            />
          </div>
          {/* Expanding white shock ring — like glass cracking outward. */}
          <div
            className="pointer-events-none fixed inset-0 z-[199] flex items-center justify-center"
            aria-hidden
          >
            <div
              style={{
                position: "absolute",
                top: "50%", left: "50%",
                width: 220, height: 220,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.95)",
                boxShadow: "0 0 60px rgba(255,255,255,0.85), inset 0 0 30px rgba(220,200,255,0.6)",
                transform: "translate(-50%, -50%) scale(0.2)",
                animation: "vc-celestial-ring 1600ms ease-out forwards",
              }}
            />
          </div>
        </>
      )}

      {/* DIVINE — full-screen golden flash on the reveal frame. Locked
          behind `prefers-reduced-motion` indirectly by the audio gate; the
          flash itself is brief (~700ms) and keyframed for one-shot fire. */}
      {flipped && card.rarity === "divine" && (
        <>
          <style>{`
            @keyframes vc-divine-flash {
              0%   { opacity: 0; }
              15%  { opacity: 0.95; }
              100% { opacity: 0; }
            }
            @keyframes vc-divine-rays {
              0%   { transform: rotate(0deg) scale(0.6); opacity: 0; }
              25%  { opacity: 0.7; }
              100% { transform: rotate(40deg) scale(1.3); opacity: 0; }
            }
          `}</style>
          <div
            className="pointer-events-none fixed inset-0 z-[199]"
            style={{
              background: "radial-gradient(circle at center, rgba(255,247,194,0.95) 0%, rgba(255,215,0,0.6) 25%, transparent 60%)",
              animation: "vc-divine-flash 850ms ease-out forwards",
            }}
            data-testid="card-reward-divine-flash"
          />
          {/* Sweeping radial god-rays — only on Divine. */}
          <div
            className="pointer-events-none fixed inset-0 z-[199] flex items-center justify-center"
            aria-hidden
          >
            <div
              style={{
                width: 1400, height: 1400,
                background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,236,94,0.18) 6deg, transparent 14deg, transparent 30deg, rgba(255,242,168,0.14) 36deg, transparent 50deg, transparent 80deg, rgba(255,215,0,0.16) 86deg, transparent 100deg, transparent 360deg)",
                mixBlendMode: "screen",
                filter: "blur(2px)",
                animation: "vc-divine-rays 2200ms ease-out forwards",
              }}
            />
          </div>
        </>
      )}
      {/* Radial glow burst — pulses while waiting, locks in after flip */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 700, height: 700,
          background: `radial-gradient(circle, ${color}66 0%, transparent 60%)`,
          filter: "blur(50px)",
          opacity: visible ? (flipped ? 0.95 : 0.55) : 0,
          transition: "opacity 700ms ease-out",
          animation: visible && !flipped ? "pulse 1.4s ease-in-out infinite" : "none",
        }}
      />
      <style>{`@keyframes pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 0.85; } }`}</style>

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        data-testid="card-reward-close"
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 bg-[#050505]/60"
        aria-label="Close"
      >
        <X size={16} />
      </button>

      {/* Reveal stack */}
      <div
        className="relative flex flex-col items-center gap-5 max-w-md w-full"
        style={{
          transform: visible ? "scale(1) translateY(0)" : "scale(0.7) translateY(40px)",
          opacity: visible ? 1 : 0,
          transition: "transform 600ms cubic-bezier(0.16, 1, 0.3, 1), opacity 600ms ease-out",
        }}
      >
        {/* Headline — fades in once the card is flipped */}
        <div
          className="text-center"
          style={{
            opacity: flipped ? 1 : 0,
            transform: flipped ? "translateY(0)" : "translateY(-10px)",
            transition: "all 500ms ease-out 200ms",
          }}
        >
          <div
            className="font-mono text-[10px] tracking-[0.4em] inline-flex items-center gap-2"
            style={{ color }}
          >
            <Sparkles size={11} /> // {headline}
          </div>
          <h2 className="font-heading font-black uppercase text-3xl text-white mt-2 leading-tight">
            {(card.rarity || "").toUpperCase()}{" "}
            <span style={{ color }}>PULL</span>
          </h2>
          <p className="font-mono text-[11px] text-zinc-300 mt-1.5 italic">
            {RARITY_BLURB[card.rarity] || "A fresh card has been added to your collection."}
          </p>
        </div>

        {/* Pre-flip teaser headline — shown while we're still on the back */}
        {!flipped && stage === "back" && (
          <div className="text-center -mb-2">
            <div className="font-mono text-[10px] tracking-[0.4em]" style={{ color }}>
              // INCOMING CARD…
            </div>
            <p className="font-mono text-[11px] text-zinc-400 mt-1">
              <span className="text-zinc-300">Click to reveal</span> · auto-flipping…
            </p>
          </div>
        )}

        {/* 3D flip container */}
        <div
          className="w-[260px] sm:w-[280px]"
          style={{ perspective: "1400px" }}
          data-testid="card-reward-card"
        >
          <div
            onClick={onCardClick}
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "5 / 7",
              transformStyle: "preserve-3d",
              transform: flipped ? "rotateY(0deg)" : "rotateY(180deg)",
              transition: "transform 1100ms cubic-bezier(0.6, 0.05, 0.3, 1)",
              cursor: stage === "loading" ? "default" : "pointer",
            }}
            title={flipped ? "View full card" : undefined}
          >
            {/* FRONT — the actual minted card */}
            <div
              style={{
                position: "absolute", inset: 0,
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
              }}
            >
              <VaultCard
                card={card}
                editionNumber={reward.edition_number}
                interactive={false}
                clickable={false}
                size="md"
              />
            </div>

            {/* BACK — sealed Vault sigil */}
            <div
              style={{
                position: "absolute", inset: 0,
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                background: `linear-gradient(135deg, #0a0a0a 0%, #1a0e22 50%, #0a0a0a 100%)`,
                border: `2px solid ${color}`,
                boxShadow: `0 0 40px ${color}55, inset 0 0 30px ${color}22`,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 14, padding: 24,
              }}
            >
              {/* corner sigils */}
              <div className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.3em]" style={{ color }}>V·J</div>
              <div className="absolute top-3 right-3 font-mono text-[9px] tracking-[0.3em]" style={{ color }}>VAULT</div>
              <div className="absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.3em]" style={{ color }}>{reward.edition_number ? `#${reward.edition_number}` : "•"}</div>
              <div className="absolute bottom-3 right-3 font-mono text-[9px] tracking-[0.3em]" style={{ color }}>SEALED</div>
              {/* Center seal */}
              <div
                className="w-32 h-32 rounded-full flex items-center justify-center relative"
                style={{
                  border: `2px solid ${color}`,
                  boxShadow: `0 0 40px ${color}88`,
                }}
              >
                <div
                  className="absolute inset-2 rounded-full"
                  style={{ border: `1px dashed ${color}80` }}
                />
                <Lock size={36} style={{ color }} />
              </div>
              <div className="font-heading uppercase text-2xl text-white tracking-tight text-center" style={{ textShadow: `0 0 20px ${color}` }}>
                Vault Sealed
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-400 text-center">
                Click to break the seal
              </div>
            </div>
          </div>
        </div>

        {/* Edition + streak pills — fade in after flip */}
        <div
          className="flex items-center gap-2 flex-wrap justify-center font-mono text-[10px] tracking-[0.2em] uppercase"
          style={{
            opacity: flipped ? 1 : 0,
            transform: flipped ? "translateY(0)" : "translateY(10px)",
            transition: "all 500ms ease-out 350ms",
          }}
        >
          <span
            className="px-2.5 py-1 border"
            style={{ color, borderColor: `${color}66` }}
          >
            COPY #{reward.edition_number}{reward.edition_limit ? ` / ${reward.edition_limit}` : ""}
          </span>
          {streak > 0 && (
            <span className="px-2.5 py-1 border border-[#ff9500]/50 text-[#ff9500]">
              🔥 {streak}-DAY STREAK
            </span>
          )}
          {streakBonus?.coins && (
            <span className="px-2.5 py-1 border border-[#ffd60a]/50 text-[#ffd60a]">
              +{streakBonus.coins} COIN BONUS
            </span>
          )}
        </div>

        {/* "Tap to inspect" hint — only post-flip */}
        <button
          type="button"
          onClick={() => setShowDetail(true)}
          data-testid="card-reward-inspect"
          className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition-colors"
          style={{
            opacity: flipped ? 1 : 0,
            pointerEvents: flipped ? "auto" : "none",
            transition: "opacity 500ms ease-out 450ms",
          }}
        >
          // Tap card to inspect //
        </button>

        {/* CTAs — only show once flipped, otherwise the click target is the back */}
        <div
          className="flex items-center gap-2 mt-1"
          style={{
            opacity: flipped ? 1 : 0,
            pointerEvents: flipped ? "auto" : "none",
            transition: "opacity 500ms ease-out 500ms",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="card-reward-dismiss"
            className="px-5 py-2.5 font-mono text-[10px] tracking-[0.25em] uppercase bg-white text-[#050505] hover:bg-zinc-200 transition-colors"
          >
            Add to collection
          </button>
          <a
            href="/vault-cards"
            data-testid="card-reward-view-all"
            className="px-5 py-2.5 font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 text-zinc-300 hover:text-white hover:border-white/40 transition-colors"
          >
            View all
          </a>
        </div>
      </div>
    </div>
  );
}
